import { useLocation } from 'react-router-dom';
import { UUID_RE } from './work-types';

const LAST_PROJECT_KEY = 'bb_last_project_id';

/** Project id from `/projects/:projectId/...` when the caller is outside <Routes>. */
export function useProjectIdFromPath(): string | undefined {
  const { pathname } = useLocation();
  // A service request record is addressed under its storage project, but it is a work type,
  // not a stream the sidebar or switcher should open.
  if (/^\/projects\/[^/]+\/service-requests\//.test(pathname)) return undefined;
  const match = pathname.match(/^\/projects\/([^/]+)/);
  const id = match?.[1];
  return id && UUID_RE.test(id) ? id : undefined;
}

export function isProjectId(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value);
}

export function getLastProjectId(): string | null {
  try {
    const raw = localStorage.getItem(LAST_PROJECT_KEY);
    return isProjectId(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function setLastProjectId(id: string): void {
  if (!isProjectId(id)) {
    throw new Error('Invalid project id.');
  }
  try {
    localStorage.setItem(LAST_PROJECT_KEY, id);
  } catch {
    return;
  }
}

export function clearLastProjectId(): void {
  try {
    localStorage.removeItem(LAST_PROJECT_KEY);
  } catch {
    return;
  }
}

/** Open a stream on its tasks page. */
export function projectWorkPath(projectId: string): string {
  if (!isProjectId(projectId)) {
    throw new Error('Invalid project id.');
  }
  return `/projects/${projectId}/tasks`;
}

/** After sign-in, open the starred project’s tasks. Otherwise Home. */
export function postLoginPath(starredProjectId: string | null | undefined): string {
  if (!isProjectId(starredProjectId)) return '/';
  return `/projects/${starredProjectId}/tasks`;
}

/** Keep the same project page kind when switching, otherwise open Tasks. */
export function projectSwitchPath(pathname: string, projectId: string): string {
  if (!isProjectId(projectId)) {
    throw new Error('Invalid project id.');
  }
  const inProject = /^\/projects\/[0-9a-fA-F-]{36}(?:\/|$)/.test(pathname);
  if (inProject) {
    if (pathname.includes('/board')) return `/projects/${projectId}/tasks`;
    if (pathname.includes('/completed') || pathname.includes('/archives')) return `/projects/${projectId}/completed`;
    if (pathname.includes('/settings')) return `/projects/${projectId}/settings`;
    if (pathname.includes('/tasks') || pathname.includes('/backlog')) {
      return `/projects/${projectId}/tasks`;
    }
  }
  return `/projects/${projectId}/tasks`;
}
